import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { QuestionnaireView, ResponseOut, ResponsePatch, VersionSummary } from "@/api/types";

export type { ResponseOut, VersionSummary } from "@/api/types";
/** The API serves the template body and answers as opaque JSON; the SPA narrows them here. */
export type QuestionnaireOut = QuestionnaireView;
type PatchBody = ResponsePatch;

export function useQuestionnaire(org: string, project: string, opts: { enabled?: boolean } = {}) {
  return useQuery<QuestionnaireOut, ApiError>({
    queryKey: queryKeys.questionnaire(org, project),
    enabled: opts.enabled ?? true,
    // A colleague may have answered or submitted while this tab was in the background.
    refetchOnWindowFocus: true,
    queryFn: async () =>
      unwrap(await api.GET("/orgs/{slug}/projects/{pslug}/questionnaire", { params: { path: { slug: org, pslug: project } } })) as QuestionnaireOut,
  });
}

export function useQuestionnaireVersions(org: string, project: string) {
  return useQuery<VersionSummary[], ApiError>({
    queryKey: queryKeys.questionnaireVersions(org, project),
    queryFn: async () =>
      unwrap(
        await api.GET("/orgs/{slug}/projects/{pslug}/questionnaire/versions", { params: { path: { slug: org, pslug: project } } }),
      ),
  });
}

export function useQuestionnaireVersion(org: string, project: string, v: number | null) {
  return useQuery<QuestionnaireOut, ApiError>({
    queryKey: queryKeys.questionnaireVersion(org, project, v ?? 0),
    enabled: v !== null,
    queryFn: async () =>
      unwrap(
        await api.GET("/orgs/{slug}/projects/{pslug}/questionnaire/versions/{version}", {
          params: { path: { slug: org, pslug: project, version: v ?? 0 } },
        }),
      ) as QuestionnaireOut,
  });
}

const emptyResponse = (): ResponseOut => ({
  id: "",
  template_id: "",
  status: "draft",
  answers: {},
  section_progress: {},
  review_flags: [],
  new_question_ids: [],
  submitted_at: null,
  submitted_by: null,
  answered_by: {},
  updated_at: null,
});

/** Partial save. The cache is updated optimistically so other views see the answer immediately. */
export function usePatchResponse(org: string, project: string, v: number) {
  const qc = useQueryClient();
  const key = queryKeys.questionnaire(org, project);
  return useMutation<ResponseOut, ApiError, PatchBody>({
    mutationFn: async (body) =>
      unwrap(
        await api.PATCH("/orgs/{slug}/projects/{pslug}/questionnaire/versions/{version}/response", {
          params: { path: { slug: org, pslug: project, version: v } },
          body,
        }),
      ) as ResponseOut,
    onMutate: (body) => {
      qc.setQueryData<QuestionnaireOut>(key, (old) => {
        if (!old?.template || old.template.version !== v) return old;
        const r = old.response ?? emptyResponse();
        return {
          ...old,
          response: {
            ...r,
            answers: { ...r.answers, ...body.answers },
            section_progress: { ...r.section_progress, ...body.section_progress },
          },
        };
      });
    },
    onSuccess: (resp) => {
      qc.setQueryData<QuestionnaireOut>(key, (old) => {
        if (!old?.template || old.template.version !== v || !old.response) return old ? { ...old, response: resp } : old;
        // Keep optimistic answers that were typed after this request left.
        return {
          ...old,
          response: {
            ...resp,
            answers: { ...resp.answers, ...old.response.answers },
            section_progress: { ...resp.section_progress, ...old.response.section_progress },
          },
        };
      });
    },
  });
}

function useResponseAction(org: string, project: string, v: number, action: "submit" | "reopen") {
  const qc = useQueryClient();
  return useMutation<ResponseOut, ApiError, void>({
    mutationFn: async () => {
      const params = { params: { path: { slug: org, pslug: project, version: v } } };
      return unwrap(
        action === "submit"
          ? await api.POST("/orgs/{slug}/projects/{pslug}/questionnaire/versions/{version}/response/submit", params)
          : await api.POST("/orgs/{slug}/projects/{pslug}/questionnaire/versions/{version}/response/reopen", params),
      ) as ResponseOut;
    },
    onSuccess: async (resp) => {
      qc.setQueryData<QuestionnaireOut>(queryKeys.questionnaire(org, project), (old) => (old ? { ...old, response: resp } : old));
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.project(org, project) }),
        qc.invalidateQueries({ queryKey: queryKeys.projects(org) }),
        qc.invalidateQueries({ queryKey: queryKeys.questionnaireVersions(org, project) }),
      ]);
    },
  });
}

export function useSubmitResponse(org: string, project: string, v: number) {
  return useResponseAction(org, project, v, "submit");
}

export function useReopenResponse(org: string, project: string, v: number) {
  return useResponseAction(org, project, v, "reopen");
}
